import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../../lib/utils";
import {
  microLabel,
  accentBarSection,
} from "../../../../lib/design-system";
import {
  createProgram,
  updateProgram,
} from "../../../../services/program.service";

const inputBase =
  "w-full px-3 py-2 rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-accent1 transition-colors";

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label
      htmlFor={htmlFor}
      className="block text-xs uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1.5"
    >
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
  </div>
);

const ProgramForm = ({ program, onClose }) => {
  const isEdit = !!program;
  const [form, setForm] = useState({
    name: program?.name || "",
    code: program?.code || "",
    department: program?.department || "",
    degreeType: program?.degreeType || "UG",
    durationYears: program?.durationYears ?? 4,
    totalSemesters: program?.totalSemesters ?? 8,
    description: program?.description || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Escape closes modal
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose(false);
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      department: form.department.trim(),
      degreeType: form.degreeType,
      durationYears: Number(form.durationYears),
      totalSemesters: Number(form.totalSemesters),
      description: form.description.trim(),
    };

    if (!payload.name || !payload.code) {
      setError("Name and code are required.");
      return;
    }
    if (!/^[A-Z0-9_]{3,30}$/.test(payload.code)) {
      setError("Code must be 3–30 chars: A–Z, 0–9, underscore only.");
      return;
    }
    if (payload.durationYears < 1 || payload.durationYears > 10) {
      setError("Duration must be between 1 and 10 years.");
      return;
    }
    if (payload.totalSemesters < 1 || payload.totalSemesters > 20) {
      setError("Total semesters must be between 1 and 20.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateProgram(program._id, payload);
        toast.success("Program updated");
      } else {
        await createProgram(payload);
        toast.success("Program created");
      }
      onClose(true);
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || "Save failed";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={() => onClose(false)}
      >
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 8, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-sm border border-gray-200 dark:border-gray-700 shadow-md w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col"
        >
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div>
              <div className={microLabel}>
                {isEdit ? "Edit" : "New"} Program
              </div>
              <h2 className="mt-1 text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                {isEdit ? program.name : "Create a new program"}
              </h2>
              <div className={cn(accentBarSection, "mt-3")} />
            </div>
            <button
              onClick={() => onClose(false)}
              className="p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="p-6 space-y-5 overflow-y-auto"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Name *" htmlFor="name">
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="B.Tech Computer Science and Engineering"
                  required
                  className={inputBase}
                />
              </Field>
              <Field
                label="Code *"
                htmlFor="code"
                hint="A–Z, 0–9, underscore. 3–30 chars."
              >
                <input
                  id="code"
                  type="text"
                  value={form.code}
                  onChange={(e) =>
                    setField("code", e.target.value.toUpperCase())
                  }
                  placeholder="BTECH_CSE"
                  required
                  className={inputBase}
                />
              </Field>
              <Field label="Department" htmlFor="department">
                <input
                  id="department"
                  type="text"
                  value={form.department}
                  onChange={(e) => setField("department", e.target.value)}
                  placeholder="CSE"
                  className={inputBase}
                />
              </Field>
              <Field label="Degree Type" htmlFor="degreeType">
                <select
                  id="degreeType"
                  value={form.degreeType}
                  onChange={(e) => setField("degreeType", e.target.value)}
                  className={inputBase}
                >
                  <option value="UG">UG (Undergraduate)</option>
                  <option value="PG">PG (Postgraduate)</option>
                </select>
              </Field>
              <Field label="Duration (years)" htmlFor="durationYears">
                <input
                  id="durationYears"
                  type="number"
                  min={1}
                  max={10}
                  value={form.durationYears}
                  onChange={(e) => setField("durationYears", e.target.value)}
                  className={inputBase}
                />
              </Field>
              <Field label="Total semesters" htmlFor="totalSemesters">
                <input
                  id="totalSemesters"
                  type="number"
                  min={1}
                  max={20}
                  value={form.totalSemesters}
                  onChange={(e) => setField("totalSemesters", e.target.value)}
                  className={inputBase}
                />
              </Field>
            </div>

            <Field label="Description" htmlFor="description">
              <textarea
                id="description"
                value={form.description}
                onChange={(e) => setField("description", e.target.value)}
                rows={3}
                placeholder="Optional internal description."
                className={cn(inputBase, "resize-y")}
              />
            </Field>

            {error && (
              <div className="p-3 rounded-sm border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                className="px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-semibold transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className={cn(
                  "px-5 py-2 rounded-sm bg-accent1 text-white font-semibold transition-opacity",
                  submitting
                    ? "opacity-60 cursor-not-allowed"
                    : "hover:opacity-90"
                )}
              >
                {submitting
                  ? "Saving…"
                  : isEdit
                  ? "Save changes"
                  : "Create program"}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProgramForm;
